import { meterService } from "../services/meterService";
import { appStore } from "../state/store";
import { can, sessionStore } from "../state/session";
import { byId } from "./dom";
import { formatNaira, formatRelative } from "./format";
import { renderTransactionList } from "./ledgerView";
import { initFundWalletFlow } from "./fundWallet";
import { initInstallApp } from "./installApp";
import type { Meter, MeterReading } from "../types";

export interface DashboardController {
  onQuickAction(name: "fund" | "buy" | "bills" | "access", handler: () => void): void;
}

/** "Good morning / afternoon / evening" by the hour in Lagos, so it's right whatever the phone's timezone. */
function lagosGreeting(): string {
  const hour = Number(
    new Intl.DateTimeFormat("en-GB", { timeZone: "Africa/Lagos", hour: "numeric", hour12: false }).format(new Date()),
  ) % 24;
  if (hour < 12) return "Good morning";
  if (hour < 17) return "Good afternoon";
  return "Good evening";
}

function firstNameOf(user: { name?: string; email?: string }): string {
  const fromName = (user.name ?? "").trim().split(/\s+/)[0] ?? "";
  const raw = fromName || (user.email ?? "").split("@")[0] || "";
  return raw ? raw.charAt(0).toUpperCase() + raw.slice(1) : "";
}

function initWelcome(): void {
  const wrap = byId("dashboard-welcome");
  const greetEl = byId("dashboard-welcome-greet");
  const subEl = byId("dashboard-welcome-sub");

  const render = (): void => {
    const user = sessionStore.user;
    if (!user) { wrap.hidden = true; return; }
    const name = firstNameOf(user);
    greetEl.textContent = "";
    greetEl.append(`${lagosGreeting()}${name ? ", " : ""}`);
    if (name) {
      const nameEl = document.createElement("span");
      nameEl.className = "dash-welcome-name";
      nameEl.textContent = name;
      greetEl.append(nameEl);
    }
    subEl.textContent = user.estateName
      ? `${user.estateName} \u00b7 here's your wallet and power at a glance.`
      : "Here's your wallet and power at a glance.";
    wrap.hidden = false;
  };

  render();
  sessionStore.subscribe(render);
  window.setInterval(render, 5 * 60 * 1000);
}

export function initDashboard(): DashboardController {
  initWelcome();
  initInstallApp();
  const fundWalletFlow = initFundWalletFlow();

  const walletBalanceValueEl = byId("wallet-balance-value");
  const walletEyeBtn = byId<HTMLButtonElement>("wallet-eye-btn");
  const withdrawWalletBtn = byId<HTMLButtonElement>("withdraw-wallet-btn");

  const quickFundBtn = byId("quick-action-fund");
  const quickBuyBtn = byId("quick-action-buy");
  const quickBillsBtn = byId("quick-action-bills");
  const quickAccessBtn = byId("quick-action-access");

  const meterCard = byId("dashboard-meter-card");
  const meterEmptyCard = byId("dashboard-meter-empty");
  const meterEmptyText = byId("dashboard-meter-empty-text");
  const meterLinkBtn = byId<HTMLButtonElement>("dashboard-link-meter-btn");
  const meterSerialDisplay = byId("meter-serial-display");
  const meterLabelDisplay = byId("meter-label-display");
  const feedStatusDisplay = byId("feed-status-display");
  const lastSyncDisplay = byId("last-sync-display");
  const refreshMeterBtn = byId<HTMLButtonElement>("refresh-meter-btn");
  const balanceValueEl = byId("balance-value");
  const balanceSub = byId("balance-sub");
  const metricVoltage = byId("metric-voltage");
  const metricAmps = byId("metric-amps");
  const metricPf = byId("metric-pf");

  const recentTxList = byId("recent-tx-list");
  const recentTxEmpty = byId("recent-tx-empty");

  let walletBalanceHidden = false;
  const handlers: Record<"fund" | "buy" | "bills" | "access", (() => void)[]> = {
    fund: [],
    buy: [],
    bills: [],
    access: [],
  };

  function onQuickAction(name: "fund" | "buy" | "bills" | "access", handler: () => void): void {
    handlers[name].push(handler);
  }

  function fire(name: "fund" | "buy" | "bills" | "access"): void {
    handlers[name].forEach((fn) => fn());
  }

  quickFundBtn.addEventListener("click", () => {
    byId("wallet-withdraw-form").hidden = true;
    fundWalletFlow.open();
    fire("fund");
  });
  byId("fund-wallet-btn").addEventListener("click", () => {
    byId("wallet-withdraw-form").hidden = true;
    fundWalletFlow.open();
    fire("fund");
  });
  quickBuyBtn.addEventListener("click", () => fire("buy"));
  quickBillsBtn.addEventListener("click", () => fire("bills"));
  quickAccessBtn.addEventListener("click", () => fire("access"));

  /* Wallet balance + role-gated withdraw */

  function renderWallet(): void {
    const { wallet, loading } = appStore.state;
    if (walletBalanceHidden) {
      walletBalanceValueEl.textContent = "\u2022\u2022\u2022\u2022";
    } else if (wallet) {
      walletBalanceValueEl.textContent = formatNaira(wallet.balanceKobo);
    } else {
      walletBalanceValueEl.textContent = loading.wallet ? "\u2026" : "\u2013\u2013";
    }
    withdrawWalletBtn.hidden = !can("wallet:withdraw");
  }

  walletEyeBtn.addEventListener("click", () => {
    walletBalanceHidden = !walletBalanceHidden;
    renderWallet();
  });

  appStore.subscribe(renderWallet);

  /* Primary linked meter readout — fetches a reading once per meter, not
     on every store change (the store notifies on ANY update, including
     the setReading call this itself makes, so without the guard below
     this would fetch forever in a feedback loop). */

  let lastFetchedMeterId: string | null = null;

  function renderMeterShell(meters: Meter[], loadingMeters: boolean): void {
    const meter = meters[0];
    if (!meter) {
      meterCard.hidden = true;
      meterEmptyCard.hidden = false;
      meterEmptyText.textContent = loadingMeters ? "Loading meter\u2026" : "No meter linked yet.";
      meterLinkBtn.hidden = loadingMeters || !can("meter:link");
      return;
    }
    meterCard.hidden = false;
    meterEmptyCard.hidden = true;

    meterSerialDisplay.textContent = meter.serial;
    meterLabelDisplay.textContent = meter.label;
    feedStatusDisplay.textContent = meter.sourceType === "grid" ? "Grid Supply" : "Facility Generator";
    feedStatusDisplay.classList.toggle("panel-card-val--active", meter.status === "active");

    if (meter.id !== lastFetchedMeterId) {
      lastFetchedMeterId = meter.id;
      void meterService.getReading(meter.id).then((reading) => appStore.setReading(reading));
    }
  }

  function applyReading(reading: MeterReading): void {
    metricVoltage.textContent = reading.voltage !== null ? reading.voltage.toFixed(1) : "\u2013";
    metricAmps.textContent = reading.amps !== null ? reading.amps.toFixed(1) : "\u2013";
    metricPf.textContent = reading.powerFactor !== null ? reading.powerFactor.toFixed(2) : "\u2013";
    lastSyncDisplay.textContent = formatRelative(reading.lastSyncAt);

    balanceValueEl.textContent = reading.unitsRemainingKwh !== null ? reading.unitsRemainingKwh.toFixed(1) : "\u2013";
    balanceSub.textContent =
      reading.estimatedDaysRemaining !== null
        ? `Estimated ${reading.estimatedDaysRemaining} day${reading.estimatedDaysRemaining === 1 ? "" : "s"} left at current draw`
        : "No recent reading available";
  }

  refreshMeterBtn.addEventListener("click", async () => {
    const meter = appStore.state.meters[0];
    if (!meter) return;
    refreshMeterBtn.disabled = true;
    refreshMeterBtn.textContent = "Syncing\u2026";
    try {
      const reading = await meterService.getReading(meter.id);
      appStore.setReading(reading);
    } finally {
      refreshMeterBtn.disabled = false;
      refreshMeterBtn.textContent = "Refresh";
    }
  });

  appStore.subscribe((state) => {
    renderMeterShell(state.meters, state.loading.meters);
    const primaryMeter = state.meters[0];
    const reading = primaryMeter ? state.readings[primaryMeter.id] : undefined;
    if (reading) applyReading(reading);
  });

  /* Recent activity preview */

  appStore.subscribe((state) => {
    const items = state.recentTransactions.slice(0, 3);
    const loading = state.loading.transactions && items.length === 0;
    if (items.length === 0 && !loading) {
      recentTxList.innerHTML = "";
      recentTxEmpty.hidden = false;
    } else {
      recentTxEmpty.hidden = true;
      renderTransactionList(recentTxList, items, loading);
    }
  });

  return { onQuickAction };
}
